/**
 * `<TemplatePicker/>` — the worked-example / starter-template picker for the
 * Map (issue #985). The highest-leverage piece of the #980 learnability work:
 * a lead facing a blank Map adopts a worked example and learns decomposition
 * BY EXAMPLE — they see a real outcome already broken into steps, then make
 * it their own.
 *
 * Shape: a calm modal over the dimmed empty workspace. It lists the library's
 * templates (name + one-line description + a quiet "N steps"); expanding one
 * fetches its full body and renders a PREVIEW of the tree as a simple indented
 * outline (title + optional description, nested) so the lead sees the SHAPE
 * before committing. "Use this template" adopts it → `useAdoptTemplate` clones
 * the tree as new roots; on success the picker closes and the canvas populates
 * via the task-nodes invalidation (the #980 next-step coach then guides the
 * lead's next move).
 *
 * It uses the soft-mint task-map palette (the same inline identity the empty
 * workspace, next-step coach, and reminders bell use) rather than the generic
 * theme tokens, so it reads as part of the Map surface.
 *
 * States are all calm + factual (§A9): a spinner while the list loads, a
 * "no examples yet" empty, and a degrade line if the endpoint isn't deployed
 * (404 → never a crash). The preview fetch shows its own small loading/error.
 *
 * Brand firewall: templates teach the WORK's shape — title/description/
 * structure only, never anything about a person, a tally, or a tier. The copy
 * stays factual and never names or ranks anyone.
 *
 * Auto-layout (§A7): the picker is chrome; adopting CREATES nodes and the
 * auto-layout places them — the picker never sets node x/y.
 */

import { useState } from "react";

import {
  type TaskTemplateSummary,
  type TemplateNode,
  useAdoptTemplate,
  useTaskTemplate,
  useTaskTemplates,
} from "../api/useTaskTemplates";
import { PALETTE } from "../tree/palette";

export interface TemplatePickerProps {
  /** The team to list / adopt templates for. */
  teamId: string | undefined;
  /** Close the picker (the scrim, the ✕, Cancel, and a successful adopt all call it). */
  onClose: () => void;
}

/** A quiet "N steps" label — singular-aware, never a score (just a count of nodes). */
function stepsLabel(count: number): string {
  return `${count} ${count === 1 ? "step" : "steps"}`;
}

export function TemplatePicker({ teamId, onClose }: TemplatePickerProps) {
  // The library loads when the picker is shown (this component only mounts then).
  const { templates, isLoading, isError } = useTaskTemplates(teamId);
  // The currently-expanded template (preview open) — only one at a time, so the
  // panel stays calm and the body fetch is scoped to the chosen example.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const adopt = useAdoptTemplate(teamId);

  const toggle = (id: string) => setExpandedId((current) => (current === id ? null : id));

  const adoptTemplate = (templateId: string) => {
    adopt.mutate(templateId, {
      // On success the task-nodes query is already invalidated by the hook;
      // closing reveals the now-populated canvas (+ the #980 next-step coach).
      onSuccess: () => onClose(),
    });
  };

  return (
    <div
      data-testid="template-picker"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      aria-label="Start from a worked example"
    >
      {/* Scrim — dims the empty workspace; clicking it closes the picker. */}
      <button
        type="button"
        data-testid="template-picker-scrim"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ backgroundColor: PALETTE.ink, opacity: 0.4 }}
      />

      <div
        className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border bg-white shadow-xl"
        style={{ borderColor: PALETTE.softBorder }}
      >
        {/* Header — factual framing of what these examples are for. */}
        <div
          className="flex items-start justify-between gap-4 border-b px-6 py-4"
          style={{ borderColor: PALETTE.softBorder, backgroundColor: PALETTE.canvas }}
        >
          <div>
            <h2 className="text-lg font-bold" style={{ color: PALETTE.ink }}>
              Start from a worked example
            </h2>
            <p className="mt-0.5 text-sm" style={{ color: PALETTE.muted }}>
              Pick an outcome that's already broken into steps. Adopt it, then make it your own.
            </p>
          </div>
          <button
            type="button"
            data-testid="template-picker-close"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-md px-2 py-1 text-sm transition-colors hover:bg-white"
            style={{ color: PALETTE.faint }}
          >
            ✕
          </button>
        </div>

        {/* Body — list / loading / empty / unavailable. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <p
              data-testid="template-picker-loading"
              className="py-8 text-center text-sm"
              style={{ color: PALETTE.muted }}
            >
              Loading examples…
            </p>
          ) : isError ? (
            <p
              data-testid="template-picker-unavailable"
              className="py-8 text-center text-sm"
              style={{ color: PALETTE.muted }}
            >
              Worked examples aren't available yet. You can still start with your own outcome.
            </p>
          ) : templates.length === 0 ? (
            <p
              data-testid="template-picker-empty"
              className="py-8 text-center text-sm"
              style={{ color: PALETTE.muted }}
            >
              No worked examples yet. Start with your own outcome instead.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {templates.map((template) => (
                <TemplateRow
                  key={template.id}
                  teamId={teamId}
                  template={template}
                  expanded={expandedId === template.id}
                  onToggle={() => toggle(template.id)}
                  onAdopt={() => adoptTemplate(template.id)}
                  adopting={adopt.isPending && adopt.variables === template.id}
                  // Disable every Adopt while one is in flight, so a lead can't
                  // double-adopt or adopt two examples into one blank Map.
                  adoptDisabled={adopt.isPending}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Footer — a quiet Cancel so the lead can back out to the blank start. */}
        <div
          className="flex justify-end border-t px-6 py-3"
          style={{ borderColor: PALETTE.softBorder, backgroundColor: PALETTE.canvas }}
        >
          <button
            type="button"
            data-testid="template-picker-cancel"
            onClick={onClose}
            className="rounded-md border px-4 py-1.5 text-xs font-semibold transition-colors hover:bg-white"
            style={{ borderColor: PALETTE.softBorder, color: PALETTE.slate }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * One template row: the summary line (name + description + quiet step count),
 * an expand toggle that reveals the tree preview, and a per-row Adopt action.
 */
function TemplateRow({
  teamId,
  template,
  expanded,
  onToggle,
  onAdopt,
  adopting,
  adoptDisabled,
}: {
  teamId: string | undefined;
  template: TaskTemplateSummary;
  expanded: boolean;
  onToggle: () => void;
  onAdopt: () => void;
  adopting: boolean;
  adoptDisabled: boolean;
}) {
  return (
    <li
      data-testid="template-row"
      data-template-id={template.id}
      className="rounded-xl border"
      style={{ borderColor: PALETTE.softBorder }}
    >
      {/* The summary line is the toggle: click anywhere to preview the shape. */}
      <button
        type="button"
        data-testid="template-row-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        <span aria-hidden className="mt-0.5 text-xs" style={{ color: PALETTE.faint }}>
          {expanded ? "▾" : "▸"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold" style={{ color: PALETTE.ink }}>
              {template.name}
            </span>
            <span
              data-testid="template-step-count"
              className="shrink-0 text-[11px]"
              style={{ color: PALETTE.faint }}
            >
              {stepsLabel(template.node_count)}
            </span>
          </span>
          <span className="mt-0.5 block text-xs" style={{ color: PALETTE.muted }}>
            {template.description}
          </span>
        </span>
      </button>

      {expanded && (
        <div
          data-testid="template-preview"
          className="border-t px-4 py-3"
          style={{ borderColor: PALETTE.softBorder, backgroundColor: "#F7FFF9" }}
        >
          <TemplatePreview teamId={teamId} templateId={template.id} />
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              data-testid="template-adopt"
              onClick={onAdopt}
              disabled={adoptDisabled}
              className="rounded-md px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: PALETTE.teal }}
            >
              {adopting ? "Adding…" : "Use this template"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * The tree preview — fetches the chosen template's full `body` lazily and
 * renders it as a simple indented outline (title + optional description,
 * nested), so the lead sees the example's SHAPE before adopting. Calm own
 * loading / unavailable states.
 */
function TemplatePreview({
  teamId,
  templateId,
}: {
  teamId: string | undefined;
  templateId: string;
}) {
  const { template, isLoading, isError } = useTaskTemplate(teamId, templateId);

  if (isLoading) {
    return (
      <p
        data-testid="template-preview-loading"
        className="text-xs"
        style={{ color: PALETTE.muted }}
      >
        Loading the example…
      </p>
    );
  }
  if (isError || !template) {
    return (
      <p
        data-testid="template-preview-unavailable"
        className="text-xs"
        style={{ color: PALETTE.muted }}
      >
        Couldn't load this example. You can still start with your own outcome.
      </p>
    );
  }

  return (
    <ol data-testid="template-preview-tree" className="flex flex-col gap-1.5">
      {template.body.map((node, i) => (
        <PreviewNode key={`${node.title}-${i}`} node={node} depth={0} />
      ))}
    </ol>
  );
}

/** One node in the preview outline; recurses for children, indenting by depth. */
function PreviewNode({ node, depth }: { node: TemplateNode; depth: number }) {
  const children = node.children ?? [];
  return (
    <li data-testid="template-preview-node" style={{ paddingLeft: `${depth * 16}px` }}>
      <div className="flex items-baseline gap-2">
        <span aria-hidden style={{ color: PALETTE.connector }}>
          •
        </span>
        <span className="text-[13px] font-medium" style={{ color: PALETTE.ink }}>
          {node.title}
        </span>
      </div>
      {node.description && (
        <p className="ml-4 text-[11px]" style={{ color: PALETTE.muted }}>
          {node.description}
        </p>
      )}
      {children.length > 0 && (
        <ol className="mt-1 flex flex-col gap-1.5">
          {children.map((child, i) => (
            <PreviewNode key={`${child.title}-${i}`} node={child} depth={depth + 1} />
          ))}
        </ol>
      )}
    </li>
  );
}
